import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui";
import { NotConnected, Panel } from "@/modules/hub-kit/HubKit";

/**
 * What creators and freelance HUDDLrs are asked for, and the rules HQ reviews
 * against. Reference content, not records: the tier table mirrors Play's
 * `lib/account.ts` TIERS, the canonical Play definition, and the freelance list
 * is served by the backend from `domain/businesses/onboarding-requirements.ts`.
 * If either changes, change it there; this screen describes it.
 */

// Only Verified is offered. Partner (₱99, 6–12%) and Ambassador (₱199, 13–25%)
// come back as new tiers after launch (owner, Play Polishing 18.2).
const TIERS = [
  {
    type: "verified_creator",
    name: "Verified",
    price: "₱49",
    audience: "None",
    commission: "1–5%, set by the venue per listing",
    powers: "Post, comment, replays, go live. Facility owners can invite them.",
    approval: "Delegated reviewer",
  },
];

const FREELANCE = [
  ["Full legal name", "Required"],
  ["Email and phone", "Required"],
  ["Freelance services offered (freelance_services)", "Required, at least one; free text allowed"],
  ["Sport(s)", "Optional (a Pilates or dance instructor may have none)"],
  ["Base city, willing to travel, years of experience", "Required"],
  ["Business structure, TIN, BIR 2303, government ID", "Required"],
  ["DTI certificate", "Only for a sole proprietorship with a trade name"],
  ["Certifications, experience, NBI clearance", "Optional"],
];

export function CreatorRequirementsTab() {
  return (
    <Stack gap={24}>
      <Panel
        title="Creator tiers"
        description="What each tier costs the creator, the audience it expects, the commission band and who can approve it. Mirrors Play's tier definitions."
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Tier</Th>
              <Th>Price</Th>
              <Th>Audience expected</Th>
              <Th>Commission</Th>
              <Th>What it unlocks</Th>
              <Th>Approval</Th>
            </Tr>
          </Thead>
          <Tbody>
            {TIERS.map((t) => (
              <Tr key={t.type}>
                <Td>{t.name}</Td>
                <Td>{t.price}</Td>
                <Td>{t.audience}</Td>
                <Td>{t.commission}</Td>
                <Td>{t.powers}</Td>
                <Td>{t.approval}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Text role="small" color="secondary" as="p">
          The audience floor is not enforced when the application is submitted. Check the applicant's followers and monthly views, shown on each application, against it before you approve.
        </Text>
      </Panel>

      <Panel
        title="Freelance HUDDLr registration (coaches, trainers, instructors)"
        description="Asked of anyone registering as an individual professional (the coach and personal-trainer business types). Reviewed under Business Center; listed here because these people also create content and run activities."
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Field</Th>
              <Th>Rule</Th>
            </Tr>
          </Thead>
          <Tbody>
            {FREELANCE.map(([field, rule]) => (
              <Tr key={field}>
                <Td>{field}</Td>
                <Td>{rule}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Panel>

      <NotConnected
        title="Not connected to Play yet"
        missing="These parts of the creator program exist on one side only. None has a placeholder number anywhere in HQ."
        needs={[
          "Tier activation payment: an approved application has no way to pay the tier price. Play removed its old Xendit screen, and the API has no player-facing activation charge. Needs a decision on whether the price is charged at approval or at activation.",
          "Identity documents (government ID and selfie): Play cannot upload them for a creator application. Verification happens out of band through Support.",
          "More-information replies: a player cannot resubmit an application that HQ asked more of. The reply goes through Support.",
          "Referral commission in Play is live: a booking through huddlplay://r/<handle> stores the referrer, and a played booking that is not a no-show writes creator_payment. HQ still creates other creator payments by hand. This tab does not list the referral rows.",
        ]}
        recommendation="Referral attribution is recorded on the booking and settled into creator_payment. The remaining gaps on this list are tier payment, identity documents, and more-information replies."
      />
    </Stack>
  );
}
